import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FirstMissionBanner from "@/components/FirstMissionBanner";
import PaymentMethodSetup from "@/components/PaymentMethodSetup";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { CATEGORIES, REGULATED_JOBS, SKILLS, fmtEUR, categoryLabel } from "@/lib/constants";
import { toast } from "sonner";
import {
    ArrowRight,
    ArrowLeft,
    CheckCircle2,
    FileText,
    Loader2,
    CreditCard,
    Sparkles,
    ShieldCheck,
    AlertCircle,
} from "lucide-react";

const STEPS = ["Métier", "Mission", "Rémunération", "Entreprise", "Devis"];

const initialMission = {
    job_category: "",
    title: "",
    description: "",
    date: "",
    end_date: "",
    same_hours_daily: true,
    start_time: "09:00",
    end_time: "17:00",
    days_count: 1,
    people_count: 1,
    address: "",
    city: "",
    postal_code: "",
    break_minutes: 30,
    meal_provided: false,
    dress_code: "",
    equipment: "",
    on_site_contact: "",
    contact_phone: "",
    urgency: "normal",
    is_night: false,
    is_sunday: false,
    is_holiday: false,
    skills: {},
    hourly_rate: null,
    professional_status: "independent",
    km_distance: 0,
    lodging_needed: false,
};

const initialCompany = {
    company_name: "",
    trade_name: "",
    siret: "",
    vat_number: "",
    hq_address: "",
    billing_address: "",
    contact_name: "",
    contact_role: "",
    phone: "",
    email: "",
    mission_address: "",
    po_reference: "",
    internal_ref: "",
    payment_terms: "30j",
};

const DRAFT_KEY = "renforteo_devis_draft_v1";

export default function Configurator() {
    const [params] = useSearchParams();
    const nav = useNavigate();
    const { user, loading: authLoading } = useAuth();
    // Restore any previous draft (e.g. after signup/login redirect back to /devis)
    const savedDraft = React.useMemo(() => {
        try {
            const d = JSON.parse(localStorage.getItem(DRAFT_KEY) || "null");
            if (!d || typeof d !== "object") return null;
            if (d.quote && (typeof d.quote.total_hours !== "number" || !Array.isArray(d.quote.lines))) d.quote = null;
            if (d.savedQuote && !d.savedQuote.quote_result) d.savedQuote = null;
            d.step = Number.isInteger(d.step) ? Math.min(Math.max(d.step, 0), 4) : 0;
            if (d.step === 4 && !d.savedQuote) d.step = 3;
            return d;
        } catch { return null; }
    }, []);
    const [step, setStep] = useState(savedDraft?.step ?? 0);
    const [mission, setMission] = useState(
        savedDraft?.mission
            ? { ...initialMission, ...savedDraft.mission }
            : { ...initialMission, job_category: params.get("cat") || "" }
    );
    const [company, setCompany] = useState(
        savedDraft?.company
            ? { ...initialCompany, ...savedDraft.company }
            : initialCompany
    );
    const [quote, setQuote] = useState(savedDraft?.quote || null);
    const [savedQuote, setSavedQuote] = useState(savedDraft?.savedQuote || null);
    const [loading, setLoading] = useState(false);
    const [signatureName, setSignatureName] = useState("");
    const [signed, setSigned] = useState(false);
    // Bonus idea E — preview of saved-team members available on this mission
    const [teamPreview, setTeamPreview] = useState([]);
    // Phase 2 pivot — SIRET INSEE lookup + saved SEPA/card
    const [siretInfo, setSiretInfo] = useState(null);
    const [siretVerifying, setSiretVerifying] = useState(false);
    const [hasPaymentMethod, setHasPaymentMethod] = useState(null);
    // Phase 5 pivot — Credit ceiling snapshot for the current company
    const [creditProfile, setCreditProfile] = useState(null);

    // Persist draft on every change so the user never loses input on
    // login/signup round-trip. Includes the SAVED QUOTE so an anonymous user
    // can review the final quote, be prompted for account creation right
    // before signing, and come back on the exact same screen.
    useEffect(() => {
        try {
            localStorage.setItem(
                DRAFT_KEY,
                JSON.stringify({ step, mission, company, quote, savedQuote }),
            );
        } catch {}
    }, [step, mission, company, quote, savedQuote]);

    // Anonymous visitors can never resume past the Rémunération step
    useEffect(() => {
        if (!authLoading && !user && step > 2) {
            setStep(2);
            setSavedQuote(null);
        }
    }, [authLoading, user, step]);

    // Prefill the "company email" field for a logged-in client so they don't
    // have to retype it — small but critical UX win.
    useEffect(() => {
        if (user && !company.email) {
            setCompany((c) => ({
                ...c,
                email: user.email || c.email,
                contact_name: c.contact_name || user.full_name || "",
            }));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.email]);

    const setM = (k, v) => setMission((m) => ({ ...m, [k]: v }));
    const toggleSkill = (k) =>
        setMission((m) => ({ ...m, skills: { ...m.skills, [k]: !m.skills[k] } }));

    // Estimate live when relevant fields ready
    useEffect(() => {
        if (
            step < 3 &&
            mission.job_category &&
            mission.start_time &&
            mission.end_time &&
            mission.city
        ) {
            const t = setTimeout(async () => {
                try {
                    const { data } = await api.post(
                        "/quotes/estimate",
                        mission
                    );
                    setQuote(data);
                } catch (_) {}
                // Optional team-availability preview (only for logged-in clients)
                if (user?.role === "client" && mission.date && mission.job_category) {
                    try {
                        const r = await api.post(
                            "/client/teams/available-for-mission",
                            {
                                job_category: mission.job_category,
                                date: mission.date,
                                city: mission.city,
                                postal_code: mission.postal_code,
                                address: mission.address,
                            },
                        );
                        setTeamPreview(r.data || []);
                    } catch (_) {
                        setTeamPreview([]);
                    }
                }
            }, 400);
            return () => clearTimeout(t);
        }
    }, [mission, step, user?.role]);

    const missingFields = useMemo(() => {
        const missing = [];
        if (step === 0) {
            if (!mission.job_category) missing.push({ key: "job_category", label: "Métier" });
        } else if (step === 1) {
            if (!mission.title?.trim()) missing.push({ key: "title", label: "Intitulé" });
            if (!mission.date) missing.push({ key: "date", label: "Date" });
            if (!mission.start_time) missing.push({ key: "start_time", label: "Heure de début" });
            if (!mission.end_time) missing.push({ key: "end_time", label: "Heure de fin" });
            if (!mission.address?.trim()) missing.push({ key: "address", label: "Adresse" });
            if (!mission.city?.trim()) missing.push({ key: "city", label: "Ville" });
            if (!mission.postal_code?.trim()) missing.push({ key: "postal_code", label: "Code postal" });
        } else if (step === 3) {
            if (!company.company_name?.trim()) missing.push({ key: "company_name", label: "Nom entreprise" });
            if (!company.siret?.trim()) missing.push({ key: "siret", label: "SIRET" });
            if (!company.contact_name?.trim()) missing.push({ key: "contact_name", label: "Contact" });
            if (!company.phone?.trim()) missing.push({ key: "phone", label: "Téléphone" });
            if (!company.email?.trim()) missing.push({ key: "email", label: "E-mail" });
            if (!company.hq_address?.trim()) missing.push({ key: "hq_address", label: "Adresse du siège" });
        }
        return missing;
    }, [step, mission, company]);

    const canProceed = missingFields.length === 0;

    const goNext = async () => {
        if (missingFields.length > 0) {
            const labels = missingFields.map((f) => f.label).join(", ");
            toast.error(`Champs manquants : ${labels}`);
            // Focus first missing field for UX (only for step 1/mission fields)
            if (step === 1 && missingFields[0]) {
                const el = document.querySelector(`[data-testid="mission-${missingFields[0].key.replace("_","-")}-input"]`);
                if (el) el.focus();
            }
            return;
        }
        // Auth gate BEFORE step 4 (Entreprise) — the user must have created an
        // account so their SIRET / RIB / billing profile lives on their own
        // space, not scattered across anonymous quote submissions.
        if (step === 2 && !user) {
            toast("Créez votre compte entreprise pour continuer", {
                description: "Votre société, votre SIRET et votre RIB seront enregistrés sur votre espace. Vos réponses précédentes sont conservées.",
            });
            window.scrollTo(0, 0);
            nav(`/inscription?next=${encodeURIComponent("/devis")}`);
            return;
        }
        if (step === 3) {
            // Save the quote. At this point the user is guaranteed to be
            // authenticated (gate above at step 2 → 3). We still tolerate
            // anonymous saves at the API layer as a defensive fallback.
            setLoading(true);
            try {
                const { data } = await api.post("/quotes", {
                    mission_input: mission,
                    company_info: company,
                });
                setSavedQuote(data);
                setQuote(data.quote_result);
                setStep(4);
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error("Quote generation failed:", err?.response?.status, err?.response?.data, err);
                const detail = err?.response?.data?.detail;
                let msg = "Erreur lors de la génération du devis.";
                if (typeof detail === "string") msg = detail;
                else if (Array.isArray(detail) && detail.length > 0) {
                    const first = detail[0];
                    msg = `Champ « ${(first.loc || []).slice(-1)[0] || ""} » invalide : ${first.msg || ""}`;
                } else if (err?.message) msg = err.message;
                toast.error(msg);
            } finally {
                setLoading(false);
            }
            return;
        }
        setStep((s) => Math.min(s + 1, STEPS.length - 1));
    };

    const goBack = () => setStep((s) => Math.max(s - 1, 0));

    const signAndPublish = async () => {
        if (!signatureName) {
            toast.error("Saisissez votre nom pour signer le devis");
            return;
        }
        if (hasPaymentMethod === false) {
            toast.error("Enregistrez un moyen de paiement (SEPA ou CB) avant de publier");
            return;
        }
        setLoading(true);
        try {
            await api.post(`/quotes/${savedQuote.id}/sign`, {
                signature_name: signatureName,
            });
            setSigned(true);
            toast.success("Devis signé — mission publiée");
            // Draft has served its purpose — wipe it so a fresh visit to /devis
            // starts clean.
            try { localStorage.removeItem(DRAFT_KEY); } catch {}
            // Deferred billing pivot: aucune redirection Stripe. Direct dashboard.
            setTimeout(() => nav("/client"), 900);
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || "Erreur lors de la publication"
            );
            setLoading(false);
        }
    };

    // When entering step 5 with a saved quote, fetch billing profile + verify SIRET
    useEffect(() => {
        if (step !== 4 || !savedQuote || !user || user.role !== "client") return;
        let cancelled = false;
        // 1. Verify SIRET via INSEE
        if (company.siret && !siretInfo) {
            setSiretVerifying(true);
            api.post("/company/verify-siret", { siret: company.siret })
                .then(({ data }) => { if (!cancelled) setSiretInfo(data); })
                .catch(() => { if (!cancelled) setSiretInfo({ verified: false, reason: "api_error" }); })
                .finally(() => { if (!cancelled) setSiretVerifying(false); });
        }
        // 2. Fetch billing profile
        api.get("/company/billing/current")
            .then(({ data }) => {
                if (cancelled) return;
                setHasPaymentMethod(!!data.billing_profile?.has_default_payment_method);
                setCreditProfile(data.billing_profile || null);
            })
            .catch(() => { if (!cancelled) setHasPaymentMethod(false); });
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step, savedQuote?.id, user?.id]);

    return (
        <div className="min-h-screen">
            <Header />
            <FirstMissionBanner />
            <div className="max-w-6xl mx-auto px-5 md:px-8 py-10">
                {/* Stepper */}
                <div
                    className="flex items-center justify-between mb-6 flex-wrap gap-3"
                    data-testid="configurator-stepper"
                >
                    {STEPS.map((label, i) => (
                        <div key={i} className="flex items-center gap-3 flex-1 min-w-[120px]">
                            <div
                                className={`stepper-dot ${i === step ? "active" : ""} ${i < step ? "done" : ""}`}
                            >
                                {i < step ? <CheckCircle2 size={16} /> : i + 1}
                            </div>
                            <div className="hidden sm:block">
                                <div className="overline">Étape {i + 1}</div>
                                <div
                                    className={`text-sm font-semibold ${i === step ? "text-[#0B192C]" : "text-slate-500"}`}
                                >
                                    {label}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Anonymous heads-up — the visitor can browse the first three
                    steps (Métier, Mission, Rémunération) without an account.
                    Before entering their SIRET / société / RIB (step 4), we ask
                    them to create their company account so this data lands on
                    their own space. */}
                {!user && (
                    <div
                        className="mb-8 flex items-start gap-3 rounded-2xl border border-[#F5A623]/40 bg-[#F5A623]/10 px-4 py-3 text-sm text-[#0B192C]"
                        data-testid="anon-preview-banner"
                    >
                        <Sparkles className="shrink-0 mt-0.5 text-[#F5A623]" size={16} />
                        <div className="leading-relaxed">
                            <b>Estimation libre pour commencer.</b> À l'étape 4
                            <b> Entreprise</b>, un compte entreprise (30&nbsp;s) sera
                            demandé pour enregistrer votre société, SIRET et RIB sur
                            votre espace personnel. Toutes vos réponses précédentes
                            sont conservées.
                        </div>
                    </div>
                )}

                <div className="grid md:grid-cols-3 gap-6">
                    {/* Form */}
                    <div className="md:col-span-2 sd-card p-6 md:p-8 fade-up">
                        {step === 0 && (
                            <div>
                                <div className="overline mb-2">
                                    Étape 1 sur 5
                                </div>
                                <h2 className="font-display text-2xl mb-6">
                                    Quel métier recherchez-vous ?
                                </h2>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                    {CATEGORIES.map((c) => {
                                        const reg = REGULATED_JOBS[c.key];
                                        return (
                                            <button
                                                key={c.key}
                                                type="button"
                                                onClick={() => setM("job_category", c.key)}
                                                className={`relative p-4 rounded-xl border text-left transition-colors ${
                                                    mission.job_category === c.key
                                                        ? "border-[#F5A623] bg-[#F5A623]/10 text-[#0B192C]"
                                                        : "border-slate-200 hover:border-[#0B192C]/30 bg-white"
                                                }`}
                                                data-testid={`cat-btn-${c.key}`}
                                            >
                                                <div className="font-semibold text-sm">
                                                    {c.label}
                                                </div>
                                                {reg && (
                                                    <div className="mt-1 text-[10px] text-[#0B192C]/70 font-medium leading-snug">
                                                        ⚠ {reg.label}
                                                    </div>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                                {mission.job_category && REGULATED_JOBS[mission.job_category] && (
                                    <div
                                        className="mt-4 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-slate-700 leading-relaxed"
                                        data-testid="regulated-job-warning"
                                    >
                                        <div className="font-semibold text-[#0B192C] mb-1">
                                            Métier réglementé — {REGULATED_JOBS[mission.job_category].label}
                                        </div>
                                        {REGULATED_JOBS[mission.job_category].detail}
                                    </div>
                                )}
                                <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4 text-xs text-slate-600 leading-relaxed">
                                    <b className="text-[#0B192C]">Nos prestataires sont tous des auto-entrepreneurs / indépendants immatriculés.</b>{" "}
                                    Chaque candidat est validé (SIRET, KBIS, identité, domicile) avant de pouvoir postuler.
                                    Certains métiers (mannequinat notamment) ne sont pas proposés car le droit français impose un statut spécifique incompatible avec l'auto-entreprise.
                                    <div className="mt-2 pt-2 border-t border-slate-200 text-emerald-800">
                                        💚 <b>Vous êtes un pro sans SIRET ?</b> On aide gratuitement les candidats à créer leur auto-entreprise (10 min sur le site du gouvernement). Écrivez à <a href="mailto:contact@renforteo.fr" className="font-semibold underline">contact@renforteo.fr</a>.
                                    </div>
                                </div>
                            </div>
                        )}

                        {step === 1 && (
                            <div>
                                <div className="overline mb-2">
                                    Étape 2 sur 5
                                </div>
                                <h2 className="font-display text-2xl mb-6">
                                    Détaillez votre mission.
                                </h2>
                                <div className="grid md:grid-cols-2 gap-4">
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-semibold mb-1">
                                            Intitulé de la mission *
                                        </label>
                                        <input
                                            required
                                            value={mission.title}
                                            onChange={(e) => setM("title", e.target.value)}
                                            placeholder="Ex: Cocktail d'entreprise soirée VIP"
                                            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus-ring bg-white"
                                            data-testid="mission-title-input"
                                        />
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-semibold mb-1">
                                            Description détaillée
                                        </label>
                                        <textarea
                                            rows={3}
                                            value={mission.description}
                                            onChange={(e) => setM("description", e.target.value)}
                                            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus-ring bg-white"
                                            data-testid="mission-description-input"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold mb-1">
                                            Date de début *
                                        </label>
                                        <input
                                            type="date"
                                            required
                                            value={mission.date}
                                            onChange={(e) => {
                                                const v = e.target.value;
                                                setM("date", v);
                                                // Keep end_date ≥ start
                                                if (!mission.end_date || (mission.end_date && mission.end_date < v)) {
                                                    setM("end_date", v);
                                                }
                                                // Recompute days_count
                                                const endV = mission.end_date && mission.end_date >= v ? mission.end_date : v;
                                                if (v && endV) {
                                                    const d0 = new Date(v);
                                                    const d1 = new Date(endV);
                                                    const days = Math.max(1, Math.round((d1 - d0) / 86400000) + 1);
                                                    setM("days_count", days);
                                                }
                                            }}
                                            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus-ring bg-white"
                                            data-testid="mission-date-input"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold mb-1">
                                            Date de fin *
                                        </label>
                                        <input
                                            type="date"
                                            required
                                            min={mission.date || undefined}
                                            value={mission.end_date || mission.date}
                                            onChange={(e) => {
                                                const v = e.target.value;
                                                setM("end_date", v);
                                                if (mission.date && v) {
                                                    const d0 = new Date(mission.date);
                                                    const d1 = new Date(v);
                                                    const days = Math.max(1, Math.round((d1 - d0) / 86400000) + 1);
                                                    setM("days_count", days);
                                                }
                                            }}
                                            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus-ring bg-white"
                                            data-testid="mission-end-date-input"
                                        />
                                        <div className="text-[11px] text-slate-500 mt-1">
                                            {mission.days_count > 1
                                                ? `Mission sur ${mission.days_count} jours consécutifs`
                                                : "Mission d'une journée"}
                                        </div>
                                    </div>
                                    {mission.days_count > 1 && (
                                        <div className="md:col-span-2">
                                            <div className="rounded-xl border border-[#F5A623]/30 bg-[#F5A623]/5 p-3">
                                                <label className="flex items-start gap-2 text-sm cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={!!mission.same_hours_daily}
                                                        onChange={(e) => setM("same_hours_daily", e.target.checked)}
                                                        className="mt-0.5"
                                                        data-testid="mission-same-hours-cb"
                                                    />
                                                    <span>
                                                        <b>Les horaires sont identiques tous les jours</b>
                                                        <div className="text-xs text-slate-500 mt-0.5">
                                                            Décochez si les horaires diffèrent selon les jours — un membre de notre équipe vous contactera pour préciser le planning.
                                                        </div>
                                                    </span>
                                                </label>
                                            </div>
                                        </div>
                                    )}
                                    <div>
                                        <label className="block text-sm font-semibold mb-1">Heure début</label>
                                        <input
                                            type="time"
                                            value={mission.start_time}
                                            onChange={(e) => setM("start_time", e.target.value)}
                                            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus-ring bg-white"
                                            data-testid="mission-start-input"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold mb-1">Heure fin</label>
                                        <input
                                            type="time"
                                            value={mission.end_time}
                                            onChange={(e) => setM("end_time", e.target.value)}
                                            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus-ring bg-white"
                                            data-testid="mission-end-input"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold mb-1">Pause (min)</label>
                                        <input
                                            type="number"
                                            min={0}
                                            value={mission.break_minutes}
                                            onChange={(e) => setM("break_minutes", parseInt(e.target.value) || 0)}
                                            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus-ring bg-white"
                                            data-testid="mission-break-input"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold mb-1">Nombre de personnes</label>
                                        <input
                                            type="number"
                                            min={1}
                                            value={mission.people_count}
                                            onChange={(e) => setM("people_count", parseInt(e.target.value) || 1)}
                                            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus-ring bg-white"
                                            data-testid="mission-people-input"
                                        />
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-semibold mb-1">Adresse *</label>
                                        <input
                                            value={mission.address}
                                            onChange={(e) => setM("address", e.target.value)}
                                            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus-ring bg-white"
                                            data-testid="mission-address-input"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold mb-1">Ville *</label>
                                        <input
                                            value={mission.city}
                                            onChange={(e) => setM("city", e.target.value)}
                                            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus-ring bg-white"
                                            data-testid="mission-city-input"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold mb-1">Code postal *</label>
                                        <input
                                            value={mission.postal_code}
                                            onChange={(e) => setM("postal_code", e.target.value)}
                                            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus-ring bg-white"
                                            data-testid="mission-postal-input"
                                        />
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-semibold mb-1">Tenue demandée</label>
                                        <input
                                            value={mission.dress_code}
                                            onChange={(e) => setM("dress_code", e.target.value)}
                                            placeholder="Ex: Noir/blanc, tenue de ville..."
                                            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus-ring bg-white"
                                        />
                                    </div>
                                    <div className="md:col-span-2 grid grid-cols-2 md:grid-cols-4 gap-2">
                                        {[
                                            ["is_night", "Horaires nuit"],
                                            ["is_sunday", "Dimanche"],
                                            ["is_holiday", "Jour férié"],
                                            ["meal_provided", "Repas fourni"],
                                        ].map(([k, l]) => (
                                            <label
                                                key={k}
                                                className={`flex items-center gap-2 p-3 rounded-xl border cursor-pointer text-sm transition-colors ${
                                                    mission[k] ? "border-[#F5A623] bg-[#F5A623]/10" : "border-slate-200"
                                                }`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={mission[k]}
                                                    onChange={(e) => setM(k, e.target.checked)}
                                                    data-testid={`mission-${k}-cb`}
                                                />
                                                <span className="font-semibold">{l}</span>
                                            </label>
                                        ))}
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-semibold mb-2">Urgence</label>
                                        <div className="flex gap-2 flex-wrap">
                                            {[
                                                ["normal", "Standard"],
                                                ["urgent", "Urgente"],
                                                ["very_urgent", "Très urgente (<24h)"],
                                            ].map(([k, l]) => (
                                                <button
                                                    key={k}
                                                    type="button"
                                                    onClick={() => setM("urgency", k)}
                                                    className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                                                        mission.urgency === k
                                                            ? "bg-[#0B192C] text-white border-[#0B192C]"
                                                            : "bg-white border-slate-200 hover:border-[#0B192C]/30"
                                                    }`}
                                                    data-testid={`urgency-${k}`}
                                                >
                                                    {l}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {step === 2 && (
                            <div>
                                <div className="overline mb-2">
                                    Étape 3 sur 5
                                </div>
                                <h2 className="font-display text-2xl mb-2">
                                    Rémunération & compétences.
                                </h2>
                                <p className="text-sm text-slate-500 mb-6">
                                    Renforteo travaille exclusivement avec des professionnels
                                    auto-entrepreneurs / indépendants immatriculés. Vous n'avez
                                    aucune déclaration sociale à faire : le prestataire vous
                                    facture en direct et gère seul ses cotisations.
                                </p>

                                <div className="grid md:grid-cols-2 gap-4 mb-6">
                                    <div>
                                        <label className="block text-sm font-semibold mb-1">
                                            Taux horaire souhaité (€/h)
                                        </label>
                                        <input
                                            type="number"
                                            step={0.5}
                                            placeholder="Ex : 16 minimum"
                                            value={mission.hourly_rate || ""}
                                            onChange={(e) => setM("hourly_rate", parseFloat(e.target.value) || null)}
                                            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus-ring bg-white"
                                            data-testid="mission-rate-input"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold mb-1">
                                            Distance A/R (km)
                                        </label>
                                        <input
                                            type="number"
                                            min={0}
                                            value={mission.km_distance}
                                            onChange={(e) => setM("km_distance", parseFloat(e.target.value) || 0)}
                                            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus-ring bg-white"
                                        />
                                    </div>
                                    <div className="md:col-span-2 flex items-end">
                                        <label className="flex items-center gap-2 text-sm">
                                            <input
                                                type="checkbox"
                                                checked={mission.lodging_needed}
                                                onChange={(e) => setM("lodging_needed", e.target.checked)}
                                            />
                                            Hébergement à prévoir
                                        </label>
                                    </div>
                                </div>

                                {/* Independent explainer banner */}
                                <div
                                    className="rounded-xl border border-[#F5A623]/40 bg-[#F5A623]/10 p-4 mb-6 flex items-start gap-3"
                                    data-testid="status-explainer"
                                >
                                    <div className="w-9 h-9 rounded-full bg-[#F5A623] text-[#0B192C] flex items-center justify-center shrink-0">
                                        <span className="font-display font-black text-sm">€</span>
                                    </div>
                                    <div className="text-sm">
                                        <div className="font-semibold text-[#0B192C] mb-1">
                                            Statut auto-entrepreneur / indépendant
                                        </div>
                                        <div className="text-slate-600 leading-relaxed">
                                            Le professionnel indépendant définit son tarif horaire
                                            HT (à partir de <b>15,01 €/h</b>). Des <b>frais Renforteo
                                            de 15 %</b> côté entreprise sont ajoutés au devis pour
                                            couvrir la mise en relation, la vérification des
                                            documents (SIRET, KBIS, identité, domicile, RC Pro) et
                                            le suivi de la mission.
                                        </div>
                                        <div className="mt-2 pt-2 border-t border-[#F5A623]/30 text-slate-700 text-xs leading-relaxed">
                                            <b>✓ Aucune déclaration à votre charge :</b> pas de contrat
                                            de travail, pas de charge patronale, pas de bulletin de
                                            paie. Le professionnel vous adresse directement sa facture
                                            après validation des heures. <b>La TVA sur la prestation
                                            dépend du statut du professionnel</b> (franchise base
                                            art. 293 B CGI : 0 % ; sinon 20 %) — le coût final vous
                                            est confirmé à la sélection du profil.
                                        </div>
                                    </div>
                                </div>

                                <div className="overline mb-3">Compétences requises</div>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                    {SKILLS.map((s) => (
                                        <label
                                            key={s.key}
                                            className={`flex items-center gap-2 p-3 rounded-xl border text-sm cursor-pointer transition-colors ${
                                                mission.skills[s.key]
                                                    ? "border-[#F5A623] bg-[#F5A623]/10"
                                                    : "border-slate-200 bg-white"
                                            }`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={!!mission.skills[s.key]}
                                                onChange={() => toggleSkill(s.key)}
                                                data-testid={`skill-${s.key}`}
                                            />
                                            <span className="font-semibold">{s.label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}

                        {step === 3 && (
                            <div>
                                <div className="overline mb-2">Étape 4 sur 5</div>
                                <h2 className="font-display text-2xl mb-6">
                                    Coordonnées de votre entreprise.
                                </h2>
                                <div className="grid md:grid-cols-2 gap-4">
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-semibold mb-1">Raison sociale *</label>
                                        <input
                                            value={company.company_name}
                                            onChange={(e) => setCompany({ ...company, company_name: e.target.value })}
                                            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus-ring bg-white"
                                            data-testid="company-name-input"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold mb-1">SIRET *</label>
                                        <input
                                            value={company.siret}
                                            onChange={(e) => setCompany({ ...company, siret: e.target.value })}
                                            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus-ring bg-white"
                                            data-testid="company-siret-input"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold mb-1">N° TVA</label>
                                        <input
                                            value={company.vat_number}
                                            onChange={(e) => setCompany({ ...company, vat_number: e.target.value })}
                                            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus-ring bg-white"
                                        />
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-semibold mb-1">Adresse du siège *</label>
                                        <input
                                            value={company.hq_address}
                                            onChange={(e) => setCompany({ ...company, hq_address: e.target.value })}
                                            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus-ring bg-white"
                                            data-testid="company-address-input"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold mb-1">Nom du responsable *</label>
                                        <input
                                            value={company.contact_name}
                                            onChange={(e) => setCompany({ ...company, contact_name: e.target.value })}
                                            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus-ring bg-white"
                                            data-testid="company-contact-input"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold mb-1">Fonction</label>
                                        <input
                                            value={company.contact_role}
                                            onChange={(e) => setCompany({ ...company, contact_role: e.target.value })}
                                            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus-ring bg-white"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold mb-1">Téléphone *</label>
                                        <input
                                            value={company.phone}
                                            onChange={(e) => setCompany({ ...company, phone: e.target.value })}
                                            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus-ring bg-white"
                                            data-testid="company-phone-input"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold mb-1">E-mail *</label>
                                        <input
                                            type="email"
                                            value={company.email}
                                            onChange={(e) => setCompany({ ...company, email: e.target.value })}
                                            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus-ring bg-white"
                                            data-testid="company-email-input"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {step === 4 && !savedQuote && (
                            <div data-testid="quote-fallback">
                                <div className="overline mb-2">Étape 5 sur 5</div>
                                <h2 className="font-display text-2xl mb-3">
                                    Devis en cours de génération…
                                </h2>
                                <p className="text-sm text-slate-500 mb-4">
                                    Si rien ne s'affiche, cliquez sur « Retour » puis à nouveau sur « Générer mon devis ».
                                </p>
                                <button
                                    onClick={() => setStep(3)}
                                    className="btn-ghost-navy text-sm"
                                    data-testid="quote-fallback-back-btn"
                                >
                                    <ArrowLeft className="inline -mt-1 mr-1" size={16} />
                                    Retour au formulaire
                                </button>
                            </div>
                        )}

                        {step === 4 && savedQuote && (
                            <div>
                                <div className="overline mb-2">Étape 5 sur 5</div>
                                <div className="flex items-start justify-between gap-3 mb-6 flex-wrap">
                                    <div>
                                        <h2 className="font-display text-2xl">
                                            Votre devis est prêt.
                                        </h2>
                                        <div className="text-sm text-slate-500 mt-1">
                                            N° <b>{savedQuote.quote_number}</b> · Valable {savedQuote.quote_result.validity_days} jours
                                        </div>
                                    </div>
                                    <a
                                        href={`${process.env.REACT_APP_BACKEND_URL}/api/quotes/${savedQuote.id}/pdf`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="btn-ghost-navy text-sm"
                                        data-testid="quote-download-pdf"
                                    >
                                        <FileText size={16} className="inline -mt-1 mr-1" />
                                        Télécharger le PDF
                                    </a>
                                </div>

                                {/* Phase 2 — SIRET INSEE verification */}
                                {siretVerifying && (
                                    <div className="sd-card p-4 mb-4 text-sm text-slate-500 flex items-center gap-2"
                                         data-testid="siret-verifying">
                                        <Loader2 className="animate-spin" size={14} />
                                        Vérification du SIRET auprès de l'INSEE / Greffes…
                                    </div>
                                )}
                                {siretInfo?.verified && (
                                    <div className="sd-card p-4 mb-4 border-l-4 border-emerald-500 bg-emerald-50/50"
                                         data-testid="siret-verified">
                                        <div className="flex items-center gap-2 mb-1">
                                            <ShieldCheck className="text-emerald-600" size={16} />
                                            <div className="text-sm font-semibold text-emerald-900">
                                                SIRET vérifié · {siretInfo.active ? "Entreprise active" : "Statut inconnu"}
                                            </div>
                                        </div>
                                        <div className="text-xs text-slate-600">
                                            {siretInfo.company_name}
                                            {siretInfo.ape_label && <> · {siretInfo.ape_label}</>}
                                            {siretInfo.greffe && <> · Greffe {siretInfo.greffe}</>}
                                        </div>
                                    </div>
                                )}
                                {siretInfo && !siretInfo.verified && (
                                    <div className="sd-card p-4 mb-4 border-l-4 border-amber-500 bg-amber-50/50"
                                         data-testid="siret-unverified">
                                        <div className="flex items-center gap-2 mb-1">
                                            <AlertCircle className="text-amber-600" size={16} />
                                            <div className="text-sm font-semibold text-amber-900">
                                                SIRET valide mais non retrouvé dans les registres publics
                                            </div>
                                        </div>
                                        <p className="text-xs text-slate-600">
                                            Le format Luhn est correct mais les données INSEE n'ont pas
                                            pu être récupérées automatiquement. Vous pouvez continuer,
                                            notre équipe vérifiera manuellement.
                                        </p>
                                    </div>
                                )}

                                {/* Phase 2 — SEPA / Card setup (only if not already saved) */}
                                {hasPaymentMethod === false && (
                                    <div
                                        className="sd-card p-6 mb-6 border-l-4 border-red-500 bg-red-50/50"
                                        data-testid="pm-cta-banner"
                                    >
                                        <div className="flex items-center gap-2 mb-2">
                                            <AlertCircle className="text-red-600" size={18} />
                                            <div className="text-base font-semibold text-red-900">
                                                Enregistrez d'abord un moyen de paiement
                                            </div>
                                        </div>
                                        <p className="text-xs text-slate-700 leading-relaxed mb-3">
                                            Renforteo prélève par SEPA (IBAN) ou carte bancaire tous
                                            les 10 jours à la clôture du cycle. Vous devez avoir un
                                            mandat SEPA ou une carte enregistrée avant de publier votre
                                            première mission. Aucun débit maintenant.
                                        </p>
                                        <a
                                            href="/client/moyen-de-paiement"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="btn-gold text-sm py-2.5 px-4 inline-flex items-center gap-2"
                                            data-testid="pm-cta-btn"
                                        >
                                            <CreditCard size={14} />
                                            Enregistrer mon RIB ou ma carte
                                        </a>
                                        <p className="text-[11px] text-slate-500 mt-2">
                                            S'ouvre dans un nouvel onglet · une fois enregistré, revenez ici et cliquez sur « Signer et publier »
                                        </p>
                                    </div>
                                )}
                                {hasPaymentMethod === true && (
                                    <div className="sd-card p-4 mb-4 border-l-4 border-emerald-500 bg-emerald-50/50"
                                         data-testid="pm-already-saved">
                                        <div className="flex items-center gap-2">
                                            <CheckCircle2 className="text-emerald-600" size={16} />
                                            <div className="text-sm font-semibold text-emerald-900">
                                                Moyen de paiement enregistré · prêt pour le prélèvement du cycle
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Phase 5 — Credit ceiling warning */}
                                {creditProfile && (() => {
                                    const limit = (creditProfile.credit_limit_cents || 0) / 100;
                                    const outstanding = (creditProfile.outstanding_cents || 0) / 100;
                                    const missionTtc = savedQuote?.quote_result?.total_ttc || 0;
                                    const projected = outstanding + missionTtc;
                                    const overLimit = projected > limit;
                                    const frozen = creditProfile.credit_status === "frozen" || creditProfile.credit_status === "banned";
                                    if (!overLimit && !frozen) return null;
                                    return (
                                        <div
                                            className="sd-card p-4 mb-4 border-l-4 border-red-500 bg-red-50/50"
                                            data-testid="credit-limit-warning"
                                        >
                                            <div className="flex items-center gap-2 mb-1">
                                                <AlertCircle className="text-red-600" size={16} />
                                                <div className="text-sm font-semibold text-red-900">
                                                    {frozen
                                                        ? "Compte suspendu — publication bloquée"
                                                        : "Plafond de crédit atteint"}
                                                </div>
                                            </div>
                                            <p className="text-xs text-slate-700 leading-relaxed">
                                                {frozen ? (
                                                    <>Votre compte est actuellement gelé. Contactez <b>support@renforteo.fr</b> pour réactivation.</>
                                                ) : (
                                                    <>
                                                        Encours actuel {outstanding.toFixed(0)} € + cette mission {missionTtc.toFixed(0)} € = <b>{projected.toFixed(0)} €</b> ·
                                                        plafond autorisé <b>{limit.toFixed(0)} €</b>. Attendez le prochain prélèvement ou demandez une augmentation.
                                                    </>
                                                )}
                                            </p>
                                        </div>
                                    );
                                })()}

                                {/* Sign */}
                                <div className={`sd-card p-6 mb-6 ${signed ? "border-green-300" : ""}`}>
                                    <div className="overline mb-2">Signature électronique</div>
                                    {signed ? (
                                        <div className="text-green-700 flex items-center gap-2">
                                            <CheckCircle2 /> Devis accepté et signé.
                                        </div>
                                    ) : (
                                        <div>
                                            <p className="text-sm text-slate-500 mb-3">
                                                Saisissez votre nom pour accepter et signer le devis.
                                            </p>
                                            <input
                                                value={signatureName}
                                                onChange={(e) => setSignatureName(e.target.value)}
                                                placeholder="Nom & Prénom"
                                                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus-ring bg-white"
                                                data-testid="signature-input"
                                            />
                                        </div>
                                    )}
                                </div>

                                {/* Deferred billing pivot — Feb 2026 */}
                                <div
                                    className="mb-4 rounded-2xl border border-[#F5A623]/30 bg-[#F5A623]/5 p-4 text-sm text-slate-700"
                                    data-testid="deferred-billing-info"
                                >
                                    <div className="font-semibold text-[#0B192C] mb-1 flex items-center gap-2">
                                        <CreditCard size={16} className="text-[#F5A623]" />
                                        Facturation groupée tous les 10 jours
                                    </div>
                                    <p className="text-xs text-slate-600 leading-relaxed">
                                        Aucun paiement à cette étape. La mission est publiée
                                        immédiatement dès signature. Toutes vos missions du cycle
                                        (1-10, 11-20 ou 21-31) sont regroupées sur une facture
                                        unique, prélevée automatiquement par SEPA ou carte enregistrée
                                        à la clôture du cycle.
                                    </p>
                                </div>
                                <div>
                                    <button
                                        disabled={loading || signed}
                                        onClick={signAndPublish}
                                        className="btn-gold w-full text-sm py-4"
                                        data-testid="sign-publish-btn"
                                    >
                                        {loading ? (
                                            <Loader2 className="inline animate-spin" size={16} />
                                        ) : signed ? (
                                            <>
                                                <CheckCircle2 className="inline -mt-1 mr-1" size={16} />
                                                Mission publiée · redirection…
                                            </>
                                        ) : (
                                            <>
                                                <CheckCircle2 className="inline -mt-1 mr-1" size={16} />
                                                Signer et publier ma mission
                                            </>
                                        )}
                                    </button>
                                    <p className="text-[11px] text-slate-400 mt-2 text-center">
                                        Total {fmtEUR(savedQuote.quote_result.total_ttc)} facturé au prochain cycle · SEPA / CB
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Live estimation sidebar */}
                    <aside className="md:col-span-1 sticky top-24 h-fit">
                        <div className="sd-card p-6">
                            <div className="overline mb-2 flex items-center gap-2">
                                <Sparkles size={12} className="text-[#F5A623]" />
                                Estimation en direct
                            </div>
                            <h3 className="font-display text-xl">
                                {categoryLabel(mission.job_category) || "Sélectionnez un métier"}
                            </h3>
                            {quote ? (
                                <div className="mt-4 space-y-2 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-slate-500">Nombre de jours</span>
                                        <b>{mission.days_count} jour{mission.days_count > 1 ? "s" : ""}</b>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-slate-500">Total heures</span>
                                        <b>{quote.total_hours.toFixed(1)} h</b>
                                    </div>
                                    {mission.break_minutes > 0 && (
                                        <div
                                            className="flex justify-between text-slate-500 text-xs bg-slate-50 -mx-2 px-2 py-1 rounded"
                                            data-testid="pause-line"
                                        >
                                            <span>⏸ Pause déduite / jour</span>
                                            <span>{mission.break_minutes} min</span>
                                        </div>
                                    )}
                                    {mission.days_count > 1 && !mission.same_hours_daily && (
                                        <div className="text-[11px] leading-snug bg-amber-50 border-l-2 border-amber-400 px-2 py-1.5 rounded text-amber-800">
                                            ⚠ Horaires variables — notre équipe vous contactera pour affiner le planning et le montant final.
                                        </div>
                                    )}
                                    <div className="flex justify-between">
                                        <span className="text-slate-500">Taux horaire réf.</span>
                                        <b>{fmtEUR(quote.hourly_rate_ref)}/h</b>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-slate-500">Prestation HT</span>
                                        <b>{fmtEUR(quote.service_ht ?? (quote.subtotal_ht - (quote.platform_fee_ht ?? 0)))}</b>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-slate-500">Frais Renforteo (15 %)</span>
                                        <b>{fmtEUR(quote.platform_fee_ht ?? quote.platform_commission ?? 0)}</b>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-slate-500">Sous-total HT</span>
                                        <b>{fmtEUR(quote.subtotal_ht)}</b>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-slate-500">TVA sur frais Renforteo (20 %)</span>
                                        <b>{fmtEUR(quote.vat_on_platform_fee ?? quote.vat_amount)}</b>
                                    </div>
                                    <div className="border-t border-slate-200 pt-3 flex justify-between">
                                        <span className="font-display font-bold">Total TTC estimé</span>
                                        <span className="font-display font-black text-xl">
                                            {fmtEUR(quote.total_ttc)}
                                        </span>
                                    </div>
                                    {quote.vat_on_service_if_assujetti > 0 && (
                                        <div
                                            className="flex justify-between items-start bg-slate-50 -mx-2 px-2 py-1.5 rounded text-xs text-slate-500"
                                            data-testid="quote-tva-assujetti-line"
                                        >
                                            <span>Si prestataire assujetti TVA → <b className="text-slate-700">+{fmtEUR(quote.vat_on_service_if_assujetti)}</b></span>
                                            <span className="text-slate-700 font-semibold whitespace-nowrap">
                                                Max&nbsp;: {fmtEUR(quote.total_ttc_if_assujetti)}
                                            </span>
                                        </div>
                                    )}
                                    <div className="pill pill-gold text-xs">
                                        Acompte {quote.deposit_percent}% · {fmtEUR(quote.deposit_amount)}
                                    </div>
                                    <p className="mt-3 text-[11px] leading-relaxed text-slate-500 bg-slate-50 border-l-2 border-[#F5A623] px-3 py-2 rounded">
                                        <b>Coût estimatif basé sur l'hypothèse « prestataire en franchise en base TVA » (art. 293 B CGI, 0 % sur la prestation).</b>
                                        Si le professionnel sélectionné est assujetti à la TVA,
                                        le total sera majoré de la ligne indiquée ci-dessus (20 % sur la prestation).
                                        La TVA sur les frais Renforteo (15 %) est toujours de 20 %.
                                        <b> Coût définitif confirmé à la sélection du profil.</b>
                                    </p>
                                    {teamPreview.length > 0 && teamPreview.some((t) => t.available_count > 0) && (
                                        <div
                                            className="mt-3 rounded-lg border border-[#F5A623]/40 bg-[#F5A623]/5 p-3"
                                            data-testid="team-availability-preview"
                                        >
                                            <div className="text-[10px] uppercase tracking-wide font-bold text-[#F5A623] mb-1.5">
                                                ⭐ Vos équipes habituelles
                                            </div>
                                            <div className="space-y-1 text-xs text-[#0B192C]">
                                                {teamPreview
                                                    .filter((t) => t.available_count > 0)
                                                    .slice(0, 3)
                                                    .map((t) => (
                                                        <div
                                                            key={t.team_id}
                                                            className="flex items-center justify-between gap-2"
                                                            data-testid={`team-preview-${t.team_id}`}
                                                        >
                                                            <span className="truncate">{t.team_name}</span>
                                                            <span className="font-bold">
                                                                {t.available_count}/{t.total_members} dispos
                                                            </span>
                                                        </div>
                                                    ))}
                                            </div>
                                            <div className="text-[10px] text-slate-500 mt-1.5 leading-snug">
                                                Estimation basée sur métier + rayon + disponibilité. Après publication, vous pourrez inviter votre équipe en un clic.
                                            </div>
                                        </div>
                                    )}
                                    <details className="mt-3">
                                        <summary className="text-xs text-slate-500 cursor-pointer">
                                            Voir le détail des lignes
                                        </summary>
                                        <div className="mt-2 space-y-1 max-h-64 overflow-auto pr-1">
                                            {quote.lines.map((l, i) => (
                                                <div key={i} className="text-xs flex justify-between gap-2">
                                                    <span className="text-slate-500 truncate">{l.label}</span>
                                                    <span className="whitespace-nowrap">{fmtEUR(l.amount)}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </details>
                                </div>
                            ) : (
                                <p className="text-sm text-slate-400 mt-3">
                                    Complétez le métier, la ville et les horaires pour voir l'estimation.
                                </p>
                            )}
                        </div>
                    </aside>
                </div>

                {/* Navigation */}
                {step < 4 && (
                    <>
                    <div className="flex justify-between mt-8">
                        <button
                            onClick={goBack}
                            disabled={step === 0}
                            className="btn-ghost-navy text-sm disabled:opacity-40"
                            data-testid="configurator-back-btn"
                        >
                            <ArrowLeft className="inline -mt-1 mr-1" size={16} />
                            Précédent
                        </button>
                        <button
                            onClick={goNext}
                            disabled={loading}
                            className={`text-sm ${
                                canProceed || loading ? "btn-gold" : "btn-gold opacity-60"
                            }`}
                            data-testid="configurator-next-btn"
                            title={
                                !canProceed
                                    ? `Champs manquants : ${missingFields.map((f) => f.label).join(", ")}`
                                    : ""
                            }
                        >
                            {loading ? (
                                <Loader2 className="inline animate-spin" size={16} />
                            ) : step === 3 ? (
                                "Générer mon devis"
                            ) : (
                                <>
                                    Continuer
                                    <ArrowRight className="inline -mt-1 ml-1" size={16} />
                                </>
                            )}
                        </button>
                    </div>
                    {!canProceed && missingFields.length > 0 && step !== 2 && (
                        <div
                            className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start gap-2"
                            data-testid="missing-fields-hint"
                        >
                            <span className="text-amber-500 font-bold">ⓘ</span>
                            <span>
                                Complétez pour continuer :{" "}
                                <b>{missingFields.map((f) => f.label).join(", ")}</b>
                            </span>
                        </div>
                    )}
                    </>
                )}
            </div>
            <Footer />
        </div>
    );
}
